import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap"
import { Form } from "react-bootstrap"
import { Link } from "react-router-dom"
import { UncontrolledTooltip } from "reactstrap"
import toastr from "toastr"
import TableContainer from "../../components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import ParentCategoryModal from "../ParentCategory/ParentCategoryModal"
import SubCategoryModal from "../SubCategoryModal/SubCategoryModal"
import apiURl from "url"

const Products = () => {
  document.title = "Products"
  const navigate = useNavigate()

  // State management
  const [isChecked, setIsChecked] = useState(false)

  const [products, setProducts] = useState([])
  const [parentCategories, setParentCategories] = useState([])
  const [subCategories, setSubCategories] = useState([])
  const [productModal, setProductModal] = useState(false)
  const [parentCategoryModal, setParentCategoryModal] = useState(false)
  const [subCategoryModal, setSubCategoryModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteID, setDeleteID] = useState(null)
  const [editProduct, setEditProduct] = useState(null)
  const [formData, setFormData] = useState({
    name: "",
    parentCategoryId: "",
    subCategoryId: "",
    price: "",
    discount: "",
    quantity: "",
    unit: "",
    description: "",
    images: [],
    status: "Active",
    featured: false,
  })

  // Handle form input changes
  const handleInputChange = e => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleDocCheckbox = () => {
    setIsChecked(prev => !prev)
    setFormData(prev => ({
      ...prev,
      featured: !isChecked,
    }))
  }

  const handleFileChange = e => {
    const files = Array.from(e.target.files)
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"]

    const allValid = files.every(file => allowedTypes.includes(file.type))

    if (!allValid) {
      toastr.error("Only JPEG, JPG, PNG, and JFIF images are allowed")
      e.target.value = "" // Clear the file input
      return
    }

    // All images are valid → Set them (replace old)
    setFormData(prev => ({
      ...prev,
      images: files,
    }))
  }

  // Fetch all products
  const fetchAllProducts = async () => {
    try {
      const response = await fetch(`${apiURl}/api/products/fetchProducts`)
      const json = await response.json()
      if (response.status === 200) setProducts(json.products.reverse())
    } catch (err) {
      console.error("Fetch Error:", err)
      toastr.error("Failed to fetch products")
    }
  }

  // Fetch parent categories
  const fetchParentCategories = async () => {
    try {
      const response = await fetch(`${apiURl}/api/categories/parents`)
      const json = await response.json()
      if (response.status === 200) setParentCategories(json.categories)
    } catch (err) {
      console.error("Fetch Error:", err)
      toastr.error("Failed to fetch parent categories")
    }
  }

  // Fetch subcategories by parent ID
  const fetchSubCategories = async parentId => {
    try {
      const response = await fetch(
        `${apiURl}/api/categories/subcategories?parentId=${parentId}`
      )
      const json = await response.json()
      if (response.status === 200) setSubCategories(json.subcategories)
    } catch (err) {
      console.error("Fetch Error:", err)
      toastr.error("Failed to fetch subcategories")
    }
  }

  // Fetch product data by ID for editing
  const fetchProductDataByID = async id => {
    try {
      const response = await fetch(`${apiURl}/api/products/fetchProductByID`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      })
      const json = await response.json()
      if (response.status === 200 && json.status === "success") {
        const product = json.ProductDataByID[0]
        if (product) {
          setFormData({
            name: product.name || "",
            parentCategoryId: product.parentCategoryId || "",
            subCategoryId: product.subCategoryId || "",
            price: product.price || "",
            discount: product.discount || "",
            quantity: product.quantity || "",
            unit: product.unit || "",
            description: product.description || "",
            images: product.images ? JSON.parse(product.images) : [],
            status: product.status || "Active",
            featured: product.featured || false,
          })
          if (product.parentCategoryId) {
            fetchSubCategories(product.parentCategoryId)
          }
          setEditProduct(product)
          setProductModal(true)
        }
      }
    } catch (err) {
      console.error("Fetch Product Error:", err)
      toastr.error("Failed to fetch product data")
    }
  }

  // Handle parent category change
  const handleParentCategoryChange = e => {
    const parentId = e.target.value
    setFormData(prev => ({
      ...prev,
      parentCategoryId: parentId,
      subCategoryId: "",
    }))
    if (parentId) {
      fetchSubCategories(parentId)
    } else {
      setSubCategories([])
    }
  }

  // Handle save product
  const handleSaveProduct = async e => {
    e.preventDefault()

    // Validation
    if (!formData.name.trim()) return toastr.error("Product Name is required")
    if (!formData.parentCategoryId)
      return toastr.error("Parent Category is required")
    if (!formData.subCategoryId) return toastr.error("Sub Category is required")
    if (!formData.price) return toastr.error("Price is required")
    if (!formData.quantity) return toastr.error("Quantity is required")
    if (!formData.unit.trim()) return toastr.error("Unit is required")
    if (!formData.description.trim())
      return toastr.error("Description is required")
    if (!formData.images.length && !editProduct)
      return toastr.error("At least one image is required")

    const method = editProduct ? "PUT" : "POST"
    const url = editProduct
      ? `${apiURl}/api/products/edit-product/${editProduct.id}`
      : `${apiURl}/api/products/add-product`

    const formPayload = new FormData()
    formPayload.append("name", formData.name)
    formPayload.append("parentCategoryId", formData.parentCategoryId)
    formPayload.append("subCategoryId", formData.subCategoryId)
    formPayload.append("price", formData.price)
    formPayload.append("discount", formData.discount || 0)
    formPayload.append("quantity", formData.quantity)
    formPayload.append("unit", formData.unit)
    formPayload.append("description", formData.description)
    formPayload.append("status", formData.status)
    formPayload.append("featured", formData.featured)
    formData.images.forEach((image, index) => {
      formPayload.append("images", image)
    })

    try {
      const response = await fetch(url, {
        method,
        body: formPayload,
      })
      const json = await response.json()

      if (response.ok) {
        toastr.success(editProduct ? "Product updated!" : "Product added!")
        fetchAllProducts()
        setProductModal(false)
        setEditProduct(null)
        setFormData({
          name: "",
          parentCategoryId: "",
          subCategoryId: "",
          price: "",
          discount: "",
          quantity: "",
          unit: "",
          description: "",
          images: [],
          status: "Active",
          featured: false,
        })
        setSubCategories([])
      } else {
        const errorText = json.message || json.error || "Failed to save product"
        toastr.error(errorText)
      }
    } catch (err) {
      console.error("Save Error:", err)
      if (err.response) {
        // If using axios or response with detailed error message
        toastr.error(err.response.data.message || "Failed to save product")
      } else {
        toastr.error(err.message || "Failed to save product")
      }
    }
  }

  // Handle delete
  const deleteProduct = async () => {
    try {
      const response = await fetch(
        `${apiURl}/api/products/delete-product/${deleteID}`,
        { method: "DELETE" }
      )
      if (response.ok) {
        toastr.success("Product deleted successfully")
        setDeleteModal(false)
        fetchAllProducts()
      } else {
        toastr.error("Failed to delete product")
      }
    } catch (err) {
      console.error("Delete Error:", err)
      toastr.error("Failed to delete product")
    }
  }

  // Initialize component
  useEffect(() => {
    fetchAllProducts()
    fetchParentCategories()
  }, [])

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "Product Name",
        accessor: "name",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Parent Category",
        accessor: "parentCategory.name",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Sub Category",
        accessor: "subCategory.name",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Price",
        accessor: "price",
        Cell: ({ value }) => <span>${value}</span>,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }) => {
          const isActive = row.original.status === "Active"
          const handleToggle = async () => {
            const newStatus = isActive ? "Not Active" : "Active"
            try {
              const res = await fetch(
                `${apiURl}/api/products/update-status/${row.original.id}`,
                {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ status: newStatus }),
                }
              )
              if (res.ok) {
                toastr.success("Status updated successfully")
                fetchAllProducts()
              } else {
                toastr.error("Failed to update status")
              }
            } catch (err) {
              toastr.error("Server error")
              console.error(err)
            }
          }
          return (
            <Form>
              <Form.Check
                type="switch"
                id={`status-switch-${row.original.id}`}
                label={
                  isActive ? (
                    <Badge className="bg-success">Active</Badge>
                  ) : (
                    <Badge className="bg-danger">Inactive</Badge>
                  )
                }
                defaultChecked={isActive}
                onChange={handleToggle}
                style={{
                  color: isActive ? "green" : "red",
                  fontWeight: "bold",
                }}
              />
            </Form>
          )
        },
      },
      {
        Header: "Action",
        accessor: "id",
        Cell: ({ value }) => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              onClick={() => fetchProductDataByID(value)}
              className="text-primary"
            >
              <i
                className="mdi mdi-eye font-size-18"
                id={`viewtooltip-${value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`viewtooltip-${value}`}
              >
                View
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={() => {
                setDeleteID(value)
                setDeleteModal(true)
              }}
            >
              <i
                className="mdi mdi-delete font-size-18"
                id={`deletetooltip-${value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`deletetooltip-${value}`}
              >
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        ),
      },
    ],
    []
  )

  let images = []

  try {
    if (typeof formData.images === "string") {
      images = JSON.parse(JSON.parse(formData.images))
    } else {
      images = formData.images
    }
  } catch (error) {
    console.error("Failed to parse images:", error)
  }

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={deleteProduct}
        onCloseClick={() => setDeleteModal(false)}
      />
      <ParentCategoryModal
        isOpen={parentCategoryModal}
        toggle={() => setParentCategoryModal(!parentCategoryModal)}
        onSave={fetchParentCategories}
      />
      <SubCategoryModal
        isOpen={subCategoryModal}
        toggle={() => setSubCategoryModal(!subCategoryModal)}
        parentCategories={parentCategories}
        onSave={() => {
          if (formData.parentCategoryId)
            fetchSubCategories(formData.parentCategoryId)
        }}
      />

      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="6">
              <h5 className="mt-2">Products</h5>
            </Col>
            <Col>
              <div className="float-end">
                <Button
                  color="primary"
                  className="btn-sm w-md"
                  onClick={() => {
                    setFormData({
                      name: "",
                      parentCategoryId: "",
                      subCategoryId: "",
                      price: "",
                      discount: "",
                      quantity: "",
                      unit: "",
                      description: "",
                      images: [],
                      status: "Active",
                      featured: false,
                    })
                    setEditProduct(null)
                    setSubCategories([])
                    setProductModal(true)
                  }}
                >
                  <i className="fa fa-fw fa-plus me-1" />
                  Create Product
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="pt-3">
            <Col lg="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={products}
                    isGlobalFilter={true}
                    isAddOptions={false}
                    className="custom-table"
                    customPageSize={10}
                    FilterColumns={false}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Product Modal */}
      <Modal
        isOpen={productModal}
        toggle={() => setProductModal(!productModal)}
        size="lg"
        centered
      >
        <Form onSubmit={handleSaveProduct}>
          <ModalHeader toggle={() => setProductModal(!productModal)}>
            {editProduct ? "Edit Product" : "Create New Product"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Product Name *</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter product name"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Parent Category *</Label>
                  <InputGroup>
                    <Input
                      type="select"
                      name="parentCategoryId"
                      value={formData.parentCategoryId}
                      onChange={handleParentCategoryChange}
                      required
                    >
                      <option value="">Select Parent Category</option>
                      {parentCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </Input>
                    <InputGroupText>
                      <Button
                        color="link"
                        onClick={() => setParentCategoryModal(true)}
                        className="p-0"
                      >
                        + Add
                      </Button>
                    </InputGroupText>
                  </InputGroup>
                </FormGroup>

                <FormGroup>
                  <Label>Sub Category *</Label>
                  <InputGroup>
                    <Input
                      type="select"
                      name="subCategoryId"
                      value={formData.subCategoryId}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.parentCategoryId}
                    >
                      <option value="">Select Sub Category</option>
                      {subCategories.map(sub => (
                        <option key={sub.id} value={sub.id}>
                          {sub.name}
                        </option>
                      ))}
                    </Input>
                    <InputGroupText>
                      <Button
                        color="link"
                        onClick={() => setSubCategoryModal(true)}
                        className="p-0"
                        disabled={!formData.parentCategoryId}
                      >
                        + Add
                      </Button>
                    </InputGroupText>
                  </InputGroup>
                </FormGroup>

                <FormGroup>
                  <Label>Price *</Label>
                  <Input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="Enter price"
                    min="0"
                    step="0.01"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Discount (Optional)</Label>
                  <Input
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleInputChange}
                    placeholder="Enter discount"
                    min="0"
                    step="0.01"
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    placeholder="Enter quantity"
                    min="0"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Unit *</Label>
                  <Input
                    type="text"
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    placeholder="Enter unit (e.g., kg, piece)"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Description *</Label>
                  <Input
                    type="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter description"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Images *</Label>
                  <Input
                    type="file"
                    name="images"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    required={!editProduct}
                  />
                </FormGroup>
                {Array.isArray(formData.images) &&
                  formData.images.length > 0 && (
                    <div className="mt-2 d-flex">
                      {formData.images.map((image, index) => (
                        <img
                          key={index}
                          src={
                            typeof image === "string"
                              ? `${apiURl}${image}`
                              : URL.createObjectURL(image)
                          }
                          alt={`Product ${index}`}
                          style={{
                            width: "70px",
                            height: "70px",
                            objectFit: "cover",
                            borderRadius: "5px",
                            marginRight: "10px",
                          }}
                        />
                      ))}
                    </div>
                  )}

                <FormGroup>
                  <Label>Status *</Label>
                  <Input
                    type="select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="Not Active">Not Active</option>
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="invalidCheck"
                    onClick={handleDocCheckbox}
                    checked={isChecked}
                  />
                  <Label className="form-check-label" htmlFor="invalidCheck">
                    Featured
                  </Label>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setFormData({
                  name: "",
                  parentCategoryId: "",
                  subCategoryId: "",
                  price: "",
                  discount: "",
                  quantity: "",
                  unit: "",
                  description: "",
                  images: [],
                  status: "Active",
                  featured: false,
                })
                setEditProduct(null)
                setSubCategories([])
                setProductModal(false)
              }}
            >
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editProduct ? "Update Product" : "Create Product"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  )
}

export default Products
